import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import classnames from 'classnames';
import DarkMasonry from '@/assets/image/vip/dark_masonry.png';
import LightMasonry from '@/assets/image/vip/light_masonry.png';
import PayResultLogo from '@/assets/image/vip/payResult-logo.png';
import AvatarDefault from '@/assets/image/subscription/avatar-default.png';
import Svip from '@/assets/image/vip/svip.png';
import Vip from '@/assets/image/vip/vip.png';
import NoVip from '@/assets/image/vip/no-vip.png';
import ProVip from '@/assets/image/vip/pro-vip.png';
import TeamVip from '@/assets/image/team/team-vip.png';
import TeamNoVip from '@/assets/image/team/team-no-vip.png';
import VipWeb from '@/assets/image/vip/vip-web.png';
import { UserInfo, VipInfo, TeamInfoResponse, Method } from '@/services/user/index.d';
import {
  getIncomeSource,
  getVipTag,
  getDurationPeriod,
  isWeb,
  closeVipSubscribeDialog,
} from '@/utils/vip';
import LoginMethods from '../login-methods';
import { getUrlParam } from '@/utils';
import {
  PurchaseScene,
  ThemeEnum,
  isFromJdServices,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { ProductType } from '@meitu/subscribe-core';
import * as native from '@/utils/native';
import { compareVersions } from 'compare-versions';
import { TabType } from '@/constants/subscription';

import styles from './index.module.css';
import { useLocaleStore } from '@/i18n/localeStore';
import { useAtom } from 'jotai';
import {
  curTabAtom,
  payProductInfoAtom,
  resultModalTitleAtom,
  seatsAtom,
  teamInfoAtom,
  themeAtom,
  userInfoAtom,
  vipInfoAtom,
  visibleBindPhoneAtom,
  visibleResultAtom,
} from '@/store';

const PayResult = () => {
  const { $t } = useLocaleStore();
  const [modalTitle] = useAtom(resultModalTitleAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [seats] = useAtom(seatsAtom);
  const [payProductInfo] = useAtom(payProductInfoAtom);
  const [curTab] = useAtom(curTabAtom);
  const [theme] = useAtom(themeAtom);
  const [, setVisibleBindPhone] = useAtom(visibleBindPhoneAtom);
  const [, setVisibleResult] = useAtom(visibleResultAtom);

  const type = getUrlParam('type');
  const isMeidou = useMemo(() => {
    return ['meidou.meitu', 'meiye.meitu', 'shejishi.entity.meitu'].includes(
      payProductInfo?.commodityId
    );
  }, [payProductInfo]);

  const [countdown, setCountdown] = useState<number>(isMeidou ? 5 : 10);

  // 是否消耗型商品（按张数购买）
  const isConsumable = payProductInfo?.productType === ProductType.Consumable;
  const commodityConfig =
    payProductInfo?.commodityConfig && JSON.parse(payProductInfo.commodityConfig);
  const consumableCount = commodityConfig?.consumableCount;
  const consumableLimit = commodityConfig?.consumableLimit;

  if (consumableLimit) {
    consumableLimit.limitType = consumableLimit?.type;
  }

  const handleClose = async () => {
    const hasBindPhone = userInfo?.login_methods?.find((item) => item.icon === Method.PHONE);
    let shouldGuide = !hasBindPhone;
    if (getIncomeSource() !== 'MTXX-WEB' && shouldGuide) {
      // 校验版本号
      const version: string = await native.getVersion();
      // 相等时返回0，小于version时返回1，大于version时返回-1
      if (getIncomeSource() === 'MTXX-PC' && compareVersions('7.0.8.5', version) > 0) {
        // Windows ：7.0.8.5及其以上版本才开放
        shouldGuide = false;
      }
      if (getIncomeSource() === 'MTXX-MAC' && compareVersions('1.7.50', version) > 0) {
        // Mac ：1.7.50及其以上版本才开放
        shouldGuide = false;
      }
    }

    // 团队版 / 京东服务场景不需要提示绑定手机号
    if (isTeamSubscription || isFromJdServices) {
      closeVipSubscribeDialog({
        success: true,
      });
      return;
    }

    if (shouldGuide) {
      // 未绑定手机号
      setVisibleBindPhone(true);
    } else {
      setVisibleResult(false);
      if (hasBindPhone) {
        closeVipSubscribeDialog({
          success: true,
        });
        return;
      }
    }
  };

  useEffect(() => {
    let timer: any = null;

    if (countdown <= 0) {
      handleClose();
      return;
    }

    timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [countdown]);

  const renderMeidouResult = (
    <>
      <div className={styles.title}>{$t((v) => v.Desc_PurchaseCompleted_Pro)}</div>
      <div className={styles.desc}>{$t((v) => v.Text_ThankYouSupport)}</div>
    </>
  );

  const vipIcon = isTeamSubscription ? TeamVip : isWeb ? VipWeb : Svip;
  const noVipIcon = isTeamSubscription ? TeamNoVip : NoVip;
  const avatar = isTeamSubscription ? teamInfo?.logo : userInfo?.avatar;
  const name = isTeamSubscription ? teamInfo?.name : userInfo?.screen_name;

  // 团队会员等级：1 团队版、2 高级团队版、3 专业团队版
  const getTeamMemberExpiryLabel = () => {
    const teamLevel = teamInfo?.vip_level ?? vipInfo?.vip_level ?? 0;
    if (teamLevel === 2) return $t((v) => v.Label_SeniorTeamMemberExpiry);
    if (teamLevel === 3) return $t((v) => v.Label_ProfessionalTeamMemberExpiry);
    return $t((v) => v.Label_TeamMemberExpiry);
  };

  const renderSubResult = (
    <>
      <div className={styles.title}>
        {isConsumable
          ? type === 'svip'
            ? $t((v) => v.Text_PurchaseBatchSuccess_MeituXiuXiu)
            : $t((v) => v.Text_PurchaseCutoutSuccess)
          : modalTitle}
      </div>
      <div className={styles.info}>
        <div className={styles.avatarBox}>
          <img className={styles.avatar} src={avatar || AvatarDefault} alt="" />
          {!getVipTag(vipInfo).hasNoBuy ? (
            <img
              className={styles.vipIcon}
              src={
                getVipTag(vipInfo).proVip
                  ? ProVip
                  : getVipTag(vipInfo).svip
                  ? vipIcon
                  : getVipTag(vipInfo).vip
                  ? Vip
                  : noVipIcon
              }
              alt=""
            />
          ) : null}
        </div>
        <div className={styles.right}>
          <div className={styles.username}>{name || '-'}</div>
          <div className={styles.desc}>
            {$t((v) => v.Label_BoundAccount)}
            {isTeamSubscription ? (
              `${teamInfo?.super_admin?.phone}`
            ) : (
              <LoginMethods userInfo={userInfo} />
            )}
          </div>
          {isConsumable ? (
            <div className={styles.desc}>
              {$t((v, { r }) => r(v.Text_PurchaseCount, [consumableCount?.count]))}
              {getDurationPeriod(consumableLimit)}
            </div>
          ) : (
            <div className={styles.desc}>
              {/* 加购场景不展示 */}
              {purchaseScene !== PurchaseScene.EXTRA ? (
                <>
                  {isTeamSubscription
                    ? getTeamMemberExpiryLabel()
                    : isWeb
                    ? getVipTag(vipInfo).proVip
                      ? $t((v) => v.Label_ProMemberExpiry)
                      : $t((v) => v.Label_StandardMemberExpiry)
                    : 'SVIP有效期：'}
                  {vipInfo?.vip_end_time
                    ? dayjs(vipInfo.vip_end_time * 1000).format('YYYY.MM.DD')
                    : ''}
                </>
              ) : null}
            </div>
          )}
          {isTeamSubscription && curTab === TabType.SUB ? (
            <div className={styles.desc}>
              {$t((v, { r }) => r(v.Text_PurchaseSeatCount, [seats]))}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={classnames(styles.module, {
        [styles.meidouModule]: isMeidou,
        [styles.consumableModule]: isConsumable,
        [styles.teamModule]: isTeamSubscription,
      })}
      style={{ borderRadius: !isWeb ? 0 : '' }}
    >
      <div className={styles.inner}>
        <div className={styles.close} onClick={handleClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M15.9515 6.35147C16.4201 5.88284 17.1799 5.88284 17.6485 6.35147C18.1172 6.8201 18.1172 7.5799 17.6485 8.04853L13.6971 12L17.6485 15.9515C18.1172 16.4201 18.1172 17.1799 17.6485 17.6485C17.1799 18.1172 16.4201 18.1172 15.9515 17.6485L12 13.6971L8.04853 17.6485C7.5799 18.1172 6.8201 18.1172 6.35147 17.6485C5.88284 17.1799 5.88284 16.4201 6.35147 15.9515L10.3029 12L6.35147 8.04853C5.88284 7.5799 5.88284 6.8201 6.35147 6.35147C6.8201 5.88284 7.5799 5.88284 8.04853 6.35147L12 10.3029L15.9515 6.35147Z"
              fill="white"
            />
          </svg>
        </div>
        {isMeidou ? renderMeidouResult : renderSubResult}
        <div className={styles.btn} onClick={handleClose}>
          {$t((v, { r }) => r(v.Button_GotItCountdown, [countdown]))}
        </div>
      </div>
    </div>
  );
};

export default PayResult;
